// notification.service.ts
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Observable, BehaviorSubject, of, forkJoin, Subject, combineLatest, throwError, lastValueFrom } from 'rxjs';
import { map, switchMap, take, filter, tap, catchError } from 'rxjs/operators';
import { UserNotificationsDTO } from '../model/UserNotificationsDTO';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { LookupService } from './lookup.service';
import { WebStorage } from '../components/auth/storage/web.storage';
import { Common } from '../utils/common';

@Injectable({ providedIn: 'root' })
export class UserNotificationService {
  [x: string]: any;
  private _unreadCount = new BehaviorSubject<number>(0);
  public unreadCount$ = this._unreadCount.asObservable();
  private unreadCountSubscription?: any;

  constructor(
    private firestore: AngularFirestore,
    private storage: AngularFireStorage,
    private lookupService: LookupService,
    private webStorage: WebStorage
  ) { }

  fetchAndUpdateCount(userId: string): void {
    // Clean up previous subscription
    this.unreadCountSubscription?.unsubscribe();

    // Calculate date 7 days ago in school timezone (end of day)
    const todayInSchoolTZ = new Date();
    const sevenDaysAgoInSchoolTZ = new Date(todayInSchoolTZ);
    sevenDaysAgoInSchoolTZ.setDate(todayInSchoolTZ.getDate() - 7);

    // Convert to UTC using Common timezone utilities
    const sevenDaysAgoUTC = Common.getUtcStartOfDay(sevenDaysAgoInSchoolTZ);

    // Timezone-aware date filtering for last 7 days

    // First try aggregated document, fallback to individual query if needed
    this.unreadCountSubscription = this.firestore
      .doc(`userCounts/${userId}`)
      .valueChanges()
      .pipe(
        map((counts: any) => counts?.notificationsUnreadCount ?? null),
        catchError(() => of(null)),
        switchMap((aggregatedCount: number | null) => {
          if (aggregatedCount !== null) {
            return of(aggregatedCount);
          } else {
            // Query recent notifications and filter unread client-side
            return this.firestore
              .collection(`userNotifications/${userId}/notifications`, ref =>
                ref.where('createdAt', '>=', firebase.firestore.Timestamp.fromDate(new Date(sevenDaysAgoUTC)))              )
              .valueChanges()
              .pipe(
                map((notifications: any[]) => {
                  const unreadNotifications = notifications.filter(n =>
                    n.read === false || n.read === null || n.read === undefined || n.read === ''
                  );
                  return unreadNotifications.length;
                }),
                catchError(() => of(0))
              );
          }
        }),
        catchError(() => of(0))
      )
      .subscribe((count: number) => {
        this._unreadCount.next(count);
      });
  }

  // Cleanup method for logout
  clearUnreadCountSubscription(): void {
    this.unreadCountSubscription?.unsubscribe();
    this.unreadCountSubscription = undefined;
  }




  getUserNotifications(userId: string): Observable<UserNotificationsDTO[]> {
    return this.firestore
      .collection(`userNotifications/${userId}/notifications`)
      .snapshotChanges()
      .pipe(
        map(actions =>
          actions.map(a => {
            const data = a.payload.doc.data() as UserNotificationsDTO;
            return { id: a.payload.doc.id, ...data };
          })
        ),
        map(notifs => notifs.sort((a, b) => b.createdAt - a.createdAt)),
        catchError(err => {
          console.error('Failed to fetch notifications:', err);
          return of([]);
        })
      );
  }

  markNotificationAsRead(userId: string, notifId: string): Promise<void> {
    return this.firestore
      .doc(`userNotifications/${userId}/notifications/${notifId}`)
      .update({ read: true });
  }

  markAllNotificationsAsRead(userId: string): Promise<void> {
    return this.firestore
      .collection(`userNotifications/${userId}/notifications`)
      .get()
      .toPromise()
      .then(snapshot => {
        if (!snapshot) {
          throw new Error('No notifications found.');
        }

        const batch = this.firestore.firestore.batch();

        snapshot.forEach(doc => {
          const data = doc.data() as { read?: boolean };

          if (!data.read) {
            batch.update(doc.ref, { read: true });
          }
        });

        return batch.commit();
      })
      .catch(error => {
        console.error('Failed to mark all notifications as read:', error);
        throw error;
      });
  }

  async createNotification(userId: any[], notification: Partial<UserNotificationsDTO>): Promise<void> {
    const promises = userId.map((id: any) => {
      const notificationUserId = `user_${id}`;
  
      const newNotification: UserNotificationsDTO = {
        title: notification.title || '',
        message: notification.message || '',
        read: false,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        ...notification
      };
  
      return this.firestore
        .collection(`userNotifications/${notificationUserId}/notifications`)
        .add(newNotification)
        .then(() => {
          return this.fetchAndUpdateCount(notificationUserId);
        });
    });
  
    // Wait for all notifications to be created and unread counts to be updated
    await Promise.all(promises);
  }
  
}
